import { useBoolean } from 'ahooks';
import { message } from 'antd';
import React, { FC, useCallback, useEffect } from 'react';
import { UseMutationResult } from 'react-query';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldPayload } from 'api/fields';
import { Field } from 'api/tasks';
import { Alert as AlertAtom } from 'components/atoms/alert';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { NotFound } from 'components/atoms/result';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { EventFormDrawer } from './event-drawer';
import { EventForm } from './event-form';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const Alert = styled(AlertAtom)`
  border: none;
  border-left: var(--blue-60) solid 4px;
  padding: var(--spacing-03);
`;

interface FieldFormPageProps {
  isEdit?: boolean;
  title?: string;
  isLoading?: boolean;
  onClose: () => void;
  objectType: Field['objectType'];
  field?: Field;
  mutation: UseMutationResult<Field, unknown, FieldPayload>;
}

export interface FieldPageProps {
  objectType: Field['objectType'];
  title?: string;
  onClose: () => void;
}

export const EventFormPage: FC<FieldFormPageProps> = (props) => {
  const {
    title,
    onClose,
    mutation,
    objectType,
    field,
    isEdit = false,
    isLoading,
  } = props;
  const [form] = Form.useForm();
  const [visible, { setFalse: closeDrawer }] = useBoolean(true);
  const errorMessage = useTranslation('settings.fields.create.error.title');
  const submitForm = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        fieldName: values.fieldLabel ?? '',
        fieldLabel: values.fieldLabel ?? '',
        description: values.description ?? '',
        objectType: FieldSectionType.Project,
        fieldType: 'KEY_EVENTS',
        fieldDataType: 'DATE',
        metaData: {
          captureKeyIntervalForFirstActivityOnly:
            values.captureKeyIntervalForFirstActivityOnly,
          allowAdminsAndOwnersToEdit: values.allowAdminsAndOwnersToEdit,
          relatedFieldId: field?.metaData?.relatedFieldId,
        },
      };

      try {
        await mutation.mutateAsync(payload, {
          onError: () => {
            message.error(errorMessage);
          },
        });
        closeDrawer();
      } catch (error) {
        logger.error('fields mutation failed', { error });
      }
    } catch (error) {
      logger.log('fields form validation failed', { error });
    }
  };

  const canShowForm = isEdit ? Boolean(field) : true;
  const subTitle = useTranslation('settings.fields.field.404.message');
  const actionText = useTranslation('settings.fields.field.404.action');
  const infoText = useTranslation('settings.events.info');

  const handleAfterVisibleChange = useCallback(() => {
    if (!visible) {
      onClose();
    }
  }, [onClose, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    queueMicrotask(() => {
      const nameElement = document.getElementById(
        'add-key-event.name',
      ) as HTMLInputElement;

      if (!nameElement) {
        return;
      }
      nameElement.focus();
    });
  });

  return (
    <EventFormDrawer
      hideFooter={isLoading || !canShowForm}
      title={title}
      visible={visible}
      isEdit={isEdit}
      isSubmitting={false}
      width={360}
      onOk={submitForm}
      onCancel={onClose}
      onClose={onClose}
      destroyOnClose
      afterVisibleChange={handleAfterVisibleChange}
    >
      {isLoading ? (
        <PageLoading />
      ) : canShowForm ? (
        <ContentWrapper>
          <EventForm form={form} objectType={objectType} field={field} />
          <Alert message={infoText} type="info" showIcon />
        </ContentWrapper>
      ) : (
        <NotFound
          subTitle={subTitle}
          linkTo={
            objectType === FieldSectionType.Project
              ? RoutePaths.projectFields
              : objectType === FieldSectionType.Task
              ? RoutePaths.taskFields
              : RoutePaths.teamMemberFields
          }
          actionText={actionText}
        />
      )}
    </EventFormDrawer>
  );
};
